import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { FileImage } from "lucide-react";
import { PdfIcon } from "../../../utils/Icons";
import { clx } from "../../../utils/Helpers";
import "./UIUploadSelectedFile.scss";

const IMAGE_EXT_PATTERN = /\.(jpe?g|png|gif|bmp|svg|webp|jfif|heic|avif)$/i;

const isPreviewUrl = (preview) =>
  typeof preview === "string" &&
  (preview.startsWith("data:image") ||
    preview.startsWith("blob:") ||
    /^https?:\/\//.test(preview));

const UIUploadSelectedFile = ({
  fileName,
  preview,
  isPdf,
  isImage,
  hasError,
  removeAriaLabel,
  onRemove,
}) => {
  const [imageLoadFailed, setImageLoadFailed] = useState(false);

  const isImageFile = useMemo(() => {
    if (
      typeof preview === "string" &&
      (preview.startsWith("data:image") ||
        preview.startsWith("blob:") ||
        /^https?:\/\//.test(preview))
    ) {
      return true;
    }
    if (
      typeof preview === "string" &&
      preview.startsWith("data:application/pdf")
    ) {
      return false;
    }
    if (isPdf) return false;
    if (isImage) return true;
    return IMAGE_EXT_PATTERN.test(fileName ?? "");
  }, [isImage, isPdf, fileName, preview]);

  const isPdfFile = useMemo(() => {
    if (
      typeof preview === "string" &&
      preview.startsWith("data:image")
    ) {
      return false;
    }
    if (
      typeof preview === "string" &&
      preview.startsWith("data:application/pdf")
    ) {
      return true;
    }
    if (isPdf) return true;
    return /\.pdf$/i.test(fileName ?? "");
  }, [isPdf, fileName, preview]);

  const canTryImagePreview = isImageFile && isPreviewUrl(preview);

  useEffect(() => {
    setImageLoadFailed(false);
  }, [preview, fileName]);

  const showImagePreview = canTryImagePreview && !imageLoadFailed;

  return (
    <Box
      className={clx(
        "ui-upload-selected-file",
        hasError && "ui-upload-selected-file--error",
      )}
      aria-busy={false}
    >
      {onRemove && (
        <IconButton
          type="button"
          className="ui-upload-selected-file__remove"
          aria-label={removeAriaLabel}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onRemove();
          }}
        >
          <CloseIcon />
        </IconButton>
      )}
      {showImagePreview ? (
        <Box
          component="img"
          src={preview}
          alt=""
          className="ui-upload-selected-file__preview"
          onError={() => setImageLoadFailed(true)}
        />
      ) : isPdfFile ? (
        <PdfIcon className="ui-upload-selected-file__file-icon" />
      ) : isImageFile ? (
        <FileImage
          className="ui-upload-selected-file__file-icon ui-upload-selected-file__file-icon--image"
          aria-hidden
        />
      ) : (
        <InsertDriveFileOutlinedIcon className="ui-upload-selected-file__file-icon" />
      )}
      <Typography className="ui-upload-selected-file__file-name">
        {fileName}
      </Typography>
    </Box>
  );
};

UIUploadSelectedFile.propTypes = {
  fileName: PropTypes.string,
  preview: PropTypes.string,
  isPdf: PropTypes.bool,
  isImage: PropTypes.bool,
  hasError: PropTypes.bool,
  removeAriaLabel: PropTypes.string,
  onRemove: PropTypes.func,
};

UIUploadSelectedFile.defaultProps = {
  fileName: "",
  preview: "",
  isPdf: false,
  isImage: false,
  hasError: false,
  removeAriaLabel: "Remove file",
  onRemove: undefined,
};

export default UIUploadSelectedFile;
